import { useState } from "react";
import { einheitLabel, zahlweiseLabel } from "../../lib/labels";
import { datum, useMehraufwand, useMehraufwandAnlegen, useMehraufwandLoeschen, useMehraufwandAbrechnen, useSchlussabrechnung, useSchlussabrechnungFestschreiben, useKatalog, useFreigabeLink, eur } from "../../hooks/useVertraege";
import type { VertragDetail, Mehraufwand } from "../../hooks/useVertraege";
import { fehlertext } from "../../lib/fehler";
import { zahl } from "../../lib/zahl";
import { Zeile } from "./teile";
import { JahresabrechnungBlock } from "./Jahresabrechnung";

// ── Tab: Abrechnung ─────────────────────────────────────────
export function TabAbrechnung({ v }: { v: VertragDetail }) {
  const hatEnde = !!v.gekuendigt_zum || v.status === "gekuendigt" || v.status === "beendet";
  return (
    <>
      <div className="pf-card" style={{ marginBottom: 16 }}>
        <Zeile l="Zahlweise" r={zahlweiseLabel(v.zahlweise)} />
        <Zeile l="Monatsrate" r={v.monats_netto ? `${eur(v.monats_netto)} netto` : "—"} strong />
        <Zeile l="Jahreswert" r={`${eur(v.jahres_netto)} netto`} />
        {v.erstes_jahr && (
          <div style={{ fontSize: 13, color: "var(--pf-ink-2)", margin: "6px 0 2px", lineHeight: 1.5 }}>
            Kunde zahlt (brutto): {v.erstes_jahr.zeile}
            {!v.erstes_jahr.volljahr && <><br />{v.erstes_jahr.folgejahr_zeile}</>}
          </div>
        )}
        {v.ist && <Zeile l="Bezahlte Raten" r={`${v.ist.raten_bezahlt} · ${eur(v.ist.umsatz_bezahlt)}`} />}
        <div style={{ fontSize: 12.5, color: "var(--pf-ink-3)", marginTop: 10 }}>
          Die Perioden-Verwaltung (Rechnung erzeugen, Zahlstatus) läuft aktuell im Planer;
          sie wird in einer nächsten Ausbaustufe hierher geholt.
        </div>
      </div>

      {v.beginn && ["aktiv", "pausiert", "gekuendigt", "beendet"].includes(v.status) && <JahresabrechnungBlock v={v} />}
      <MehraufwandBlock v={v} />
      {hatEnde && <SchlussabrechnungBlock v={v} />}
    </>
  );
}

/**
 * Mehraufwand — was über den Vertrag hinaus angefallen ist.
 *
 * Er fasst die Monatsrate NICHT an. Die ist beim Kunden oft ein Dauerauftrag;
 * sie zu verändern, weil eine Fuhre Grünschnitt mehr anfiel, bricht seine
 * Zahlung und unsere Vertragsdokumente zugleich. Abgerechnet wird deshalb
 * gebündelt als eigene Rechnung.
 */
export function MehraufwandBlock({ v }: { v: VertragDetail }) {
  const { data: posten = [] } = useMehraufwand(v.id);
  const { data: katalog = [] } = useKatalog();
  const anlegen = useMehraufwandAnlegen(v.id);
  const loeschen = useMehraufwandLoeschen(v.id);
  const abrechnen = useMehraufwandAbrechnen(v.id);
  const [offenForm, setOffenForm] = useState(false);
  const [meldung, setMeldung] = useState("");
  const [fehler, setFehler] = useState("");

  const offen = posten.filter(m => !m.abgerechnet_am);
  const summeOffen = offen.reduce((s, m) => s + Number(m.betrag_netto), 0);
  const ohneVermerk = offen.filter(m => m.beauftragung_noetig).length;

  async function lauf(fn: () => Promise<unknown>, text: string) {
    setFehler(""); setMeldung("");
    try { await fn(); setMeldung(text); }
    catch (e) { setFehler(fehlertext(e) || "Fehlgeschlagen."); }
  }

  return (
    <div className="pf-card" style={{ marginBottom: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 10 }}>
        <div style={{ fontWeight: 700 }}>Mehraufwand</div>
        <button className="pf-btn is-ghost is-small" onClick={() => setOffenForm(o => !o)}>
          {offenForm ? "Abbrechen" : "＋ Erfassen"}
        </button>
      </div>
      <div style={{ fontSize: 12.5, color: "var(--pf-ink-3)", marginTop: 4, lineHeight: 1.5 }}>
        Zusätzliche Leistungen, Mehrmengen und Material — außerhalb der Jahresvergütung.
        Wird als eigene Rechnung gestellt, damit der Zahlrhythmus unberührt bleibt.
      </div>

      {offenForm && (
        <MehraufwandForm
          katalog={katalog} faktor={Number(v.faktor) || 1}
          onSpeichern={async body => {
            await lauf(() => anlegen.mutateAsync(body), "Posten erfasst.");
            setOffenForm(false);
          }} />
      )}

      {posten.length === 0 ? (
        <div className="pf-empty" style={{ marginTop: 12 }}>Noch kein Mehraufwand erfasst.</div>
      ) : (
        <div style={{ marginTop: 12 }}>
          {posten.map(m => (
            <MehraufwandZeile key={m.id} m={m} vertragId={v.id}
              onLoeschen={() => lauf(() => loeschen.mutateAsync(m.id), "Posten entfernt.")} />
          ))}
        </div>
      )}

      {offen.length > 0 && (
        <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid var(--pf-line)" }}>
          <Zeile l={`Offen (${offen.length} ${offen.length === 1 ? "Posten" : "Posten"})`}
            r={`${eur(summeOffen)} netto`} strong />
          {ohneVermerk > 0 && (
            <div style={{ fontSize: 12.5, color: "var(--pf-ink-2)", marginTop: 6, lineHeight: 1.5 }}>
              {ohneVermerk === 1 ? "Ein Posten liegt" : `${ohneVermerk} Posten liegen`} über
              75 € netto und {ohneVermerk === 1 ? "hat" : "haben"} keinen Beauftragungsvermerk.
              § 6 des Vertrages sieht dort eine Beauftragung in Textform vor — kurz bestätigen
              lassen und am Posten vermerken.
            </div>
          )}
          <button className="pf-btn" style={{ marginTop: 10 }} disabled={abrechnen.isPending}
            onClick={() => lauf(() => abrechnen.mutateAsync(), "Als eigene Rechnung erfasst.")}>
            {abrechnen.isPending ? "Rechne ab…" : `Als eigene Rechnung abrechnen (${eur(summeOffen)} netto)`}
          </button>
        </div>
      )}
      {meldung && <div style={{ fontSize: 12.5, marginTop: 8, color: "var(--pf-ink-3)" }}>{meldung}</div>}
      {fehler && <div style={{ fontSize: 12.5, marginTop: 8, color: "var(--pf-red)" }}>{fehler}</div>}
    </div>
  );
}

export function MehraufwandZeile({ m, vertragId, onLoeschen }: {
  m: Mehraufwand; vertragId: string; onLoeschen: () => void;
}) {
  const erledigt = !!m.abgerechnet_am;
  const link = useFreigabeLink(vertragId);
  const [linkFehler, setLinkFehler] = useState("");
  const freigabe = FREIGABE_TEXT[m.freigabe_status] ?? "";
  return (
    <div style={{
      display: "flex", justifyContent: "space-between", gap: 10, padding: "8px 0",
      borderTop: "1px solid var(--pf-line)", opacity: erledigt ? 0.6 : 1,
    }}>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontWeight: 600, fontSize: 14 }}>
          {m.titel}
          {m.beauftragung_noetig && !erledigt && (
            <span style={{ color: "var(--pf-red)", fontWeight: 400, fontSize: 12.5 }}>
              {" "}· Beauftragung vermerken
            </span>
          )}
        </div>
        <div style={{ fontSize: 12.5, color: "var(--pf-ink-3)" }}>
          {datum(m.datum)} · {zahl(m.menge)} {einheitLabel(m.einheit)} × {eur(m.einzelpreis_netto)}
          {m.beauftragung ? ` · beauftragt: ${m.beauftragung}` : ""}
          {erledigt ? ` · abgerechnet ${datum(m.abgerechnet_am)}` : ""}
        </div>
        {m.notiz && <div style={{ fontSize: 12.5, color: "var(--pf-ink-3)" }}>{m.notiz}</div>}
        {freigabe && (
          <div style={{ fontSize: 12.5, color: "var(--pf-ink-2)" }}>{freigabe}</div>
        )}
        {!erledigt && m.freigabe_status !== "erteilt" && (
          <button className="pf-btn is-ghost is-small" style={{ marginTop: 6 }}
            disabled={link.isPending}
            onClick={async () => {
              setLinkFehler("");
              try { await link.mutateAsync(m.id); }
              catch (e) { setLinkFehler(fehlertext(e) || "Fehlgeschlagen."); }
            }}>
            {link.isPending ? "Sende…"
              : m.freigabe_status === "offen" ? "Freigabe erneut senden" : "Freigabe anfragen"}
          </button>
        )}
        {linkFehler && (
          <div style={{ fontSize: 12.5, color: "var(--pf-red)", marginTop: 4 }}>{linkFehler}</div>
        )}
      </div>
      <div style={{ textAlign: "right", whiteSpace: "nowrap" }}>
        <div style={{ fontWeight: 700 }}>{eur(m.betrag_netto)}</div>
        {!erledigt && (
          <button className="pf-btn is-ghost is-small" style={{ marginTop: 4, color: "var(--pf-red)" }}
            onClick={onLoeschen}>entfernen</button>
        )}
      </div>
    </div>
  );
}

/** Was der Kunde zum Freigabe-Link gesagt hat. „nicht_noetig" bleibt stumm —
 *  unter 75 € netto verlangt § 6 keine Textform, da wäre jeder Hinweis Lärm. */
const FREIGABE_TEXT: Record<string, string> = {
  offen: "Freigabe angefragt — wartet auf den Kunden",
  erteilt: "vom Kunden beauftragt (Link, mit Zeitstempel)",
  abgelehnt: "vom Kunden abgelehnt — nicht ausführen",
};

/** Erfassungsmaske. Der Titel ist eine Katalogsuche wie im Wizard — ein Treffer
 *  bringt Einheit und Preis mit, und zwar den Preis MIT Vertragsfaktor, damit er
 *  zu dem passt, was in § 6 des Vertrages abgedruckt ist. */
export function MehraufwandForm({ katalog, faktor, onSpeichern }: {
  katalog: { id: string; titel: string; einheit: string; vk_netto: string }[];
  faktor: number;
  onSpeichern: (b: {
    titel: string; menge: number; einheit: string; einzelpreis_netto: number;
    art: string; datum: string; notiz: string; beauftragung: string; leistung_id: string | null;
  }) => void;
}) {
  const [titel, setTitel] = useState("");
  const [menge, setMenge] = useState(1);
  const [einheit, setEinheit] = useState("pauschal");
  const [preis, setPreis] = useState(0);
  const [art, setArt] = useState("zusatzleistung");
  const [tag, setTag] = useState(new Date().toISOString().slice(0, 10));
  const [notiz, setNotiz] = useState("");
  const [beauftragung, setBeauftragung] = useState("");
  const [katId, setKatId] = useState<string | null>(null);

  const treffer = titel.trim().length >= 2
    ? katalog.filter(k => k.titel.toLowerCase().includes(titel.trim().toLowerCase())).slice(0, 5)
    : [];
  const betrag = Math.round(menge * preis * 100) / 100;

  return (
    <div className="pf-card" style={{ marginTop: 12 }}>
      <label className="pf-field"><span>Leistung</span>
        <input className="pf-input" value={titel} placeholder="suchen oder frei eintippen…"
          onChange={e => { setTitel(e.target.value); setKatId(null); }} /></label>
      {treffer.length > 0 && !katId && (
        <div style={{ marginTop: 6 }}>
          {treffer.map(k => (
            <button key={k.id} className="pf-row" onClick={() => {
              setTitel(k.titel); setEinheit(k.einheit); setKatId(k.id);
              setPreis(Math.round(Number(k.vk_netto) * faktor * 100) / 100);
            }}>
              <div className="pf-row-main" style={{ textAlign: "left", fontSize: 13.5 }}>
                {k.titel}
                <span style={{ color: "var(--pf-ink-3)" }}>
                  {" "}· {eur(Math.round(Number(k.vk_netto) * faktor * 100) / 100)} / {einheitLabel(k.einheit)}
                </span>
              </div>
            </button>
          ))}
        </div>
      )}
      <div className="pf-grid-2" style={{ marginTop: 10 }}>
        <label className="pf-field"><span>Menge</span>
          <input className="pf-input" type="number" step="0.01" value={menge}
            onChange={e => setMenge(Number(e.target.value) || 0)} /></label>
        <label className="pf-field"><span>Einheit</span>
          <input className="pf-input" value={einheitLabel(einheit)}
            onChange={e => setEinheit(e.target.value)} /></label>
        <label className="pf-field"><span>Preis je Einheit netto</span>
          <input className="pf-input" type="number" step="0.01" value={preis}
            onChange={e => setPreis(Number(e.target.value) || 0)} /></label>
        <label className="pf-field"><span>Datum</span>
          <input className="pf-input" type="date" value={tag}
            onChange={e => setTag(e.target.value)} /></label>
        <label className="pf-field"><span>Art</span>
          <select className="pf-input" value={art} onChange={e => setArt(e.target.value)}>
            <option value="zusatzleistung">Zusätzliche Leistung</option>
            <option value="mehrmenge">Mehrmenge zu einer Vertragsposition</option>
            <option value="material">Material</option>
            <option value="ausfall">Fehlfahrt / Zugang verwehrt</option>
          </select></label>
        <label className="pf-field"><span>Beauftragt durch</span>
          <input className="pf-input" value={beauftragung} placeholder="z. B. telefonisch 12.08."
            onChange={e => setBeauftragung(e.target.value)} /></label>
      </div>
      <label className="pf-field" style={{ marginTop: 10 }}><span>Notiz</span>
        <input className="pf-input" value={notiz} onChange={e => setNotiz(e.target.value)} /></label>

      {betrag > 75 && !beauftragung.trim() && (
        <div style={{ fontSize: 12.5, color: "var(--pf-ink-2)", marginTop: 10, lineHeight: 1.5 }}>
          Über 75 € netto sieht § 6 des Vertrages eine Beauftragung in Textform vor.
          Kurz bestätigen lassen und oben vermerken — sonst steht im Vertrag eine Zusage,
          die nirgends belegt ist.
        </div>
      )}

      <button className="pf-btn" style={{ marginTop: 12 }} disabled={!titel.trim() || betrag <= 0}
        onClick={() => onSpeichern({
          titel: titel.trim(), menge, einheit, einzelpreis_netto: preis, art,
          datum: tag, notiz, beauftragung, leistung_id: katId,
        })}>
        {betrag > 0 ? `Erfassen — ${eur(betrag)} netto` : "Erfassen"}
      </button>
    </div>
  );
}

/**
 * Schlussabrechnung — die Spitze zwischen erbrachter Leistung und berechneter Rate.
 *
 * Erscheint erst, wenn der Vertrag ein Ende hat. Vorschau immer, Festschreiben
 * auf Knopfdruck: was hier entsteht, geht an den Kunden.
 */
export function SchlussabrechnungBlock({ v }: { v: VertragDetail }) {
  const { data: s } = useSchlussabrechnung(v.id);
  const festschreiben = useSchlussabrechnungFestschreiben(v.id);
  const [meldung, setMeldung] = useState("");
  const [fehler, setFehler] = useState("");
  if (!s) return null;

  const diff = Number(s.differenz_netto);
  const farbe = s.richtung === "nachzahlung" ? "var(--pf-accent)"
    : s.richtung === "gutschrift" ? "var(--pf-red)" : "var(--pf-ink-3)";

  return (
    <div className="pf-card" style={{ borderLeft: `3px solid ${farbe}` }}>
      <div style={{ fontWeight: 700, marginBottom: 6 }}>
        Schlussabrechnung {s.ende ? `zum ${datum(s.ende)}` : ""}
      </div>
      <Zeile l="Erbrachte Leistungen" r={`${eur(s.leistungen_netto)} netto`} />
      <Zeile l="Berechnete Raten" r={`${eur(s.berechnet_netto)} netto`} />
      <Zeile l={s.richtung === "gutschrift" ? "Gutschrift" : "Nachzahlung"}
        r={`${eur(Math.abs(diff))} netto`} strong />
      <div style={{ fontSize: 12.5, color: "var(--pf-ink-2)", marginTop: 8, lineHeight: 1.55 }}>
        {s.hinweis}
      </div>

      <details style={{ marginTop: 12 }}>
        <summary style={{ cursor: "pointer", fontSize: 13.5, fontWeight: 600 }}>
          Was gerechnet wurde ({s.zeilen.filter(z => z.erbracht > 0).length} von {s.zeilen.length} Positionen erbracht)
        </summary>
        <div style={{ marginTop: 8 }}>
          {s.zeilen.map(z => (
            <div key={z.leistung_id} style={{
              display: "flex", justifyContent: "space-between", gap: 10,
              padding: "6px 0", borderTop: "1px solid var(--pf-line)",
              opacity: z.erbracht > 0 ? 1 : 0.55,
            }}>
              <div style={{ fontSize: 13.5 }}>
                {z.titel}
                <span style={{ color: "var(--pf-ink-3)" }}>
                  {" "}· {z.erbracht} von {z.geplant} Durchgängen
                </span>
              </div>
              <div style={{ whiteSpace: "nowrap", fontWeight: 600 }}>{eur(z.betrag_netto)}</div>
            </div>
          ))}
        </div>
      </details>

      {s.perioden_zu_stornieren.length > 0 && (
        <div style={{ fontSize: 12.5, color: "var(--pf-ink-2)", marginTop: 10, lineHeight: 1.5 }}>
          {s.perioden_zu_stornieren.length} Abrechnungsperiode
          {s.perioden_zu_stornieren.length === 1 ? "" : "n"} liegen nach dem Vertragsende und
          werden beim Festschreiben storniert.
        </div>
      )}

      {s.festgeschrieben ? (
        <div style={{ fontSize: 13, marginTop: 12, color: "var(--pf-ink-2)", lineHeight: 1.5 }}>
          <strong>Festgeschrieben</strong> als {s.festgeschrieben.periode} über{" "}
          {eur(s.festgeschrieben.soll_netto)} netto — steht unter den fälligen Perioden.
        </div>
      ) : (
        <button className="pf-btn" style={{ marginTop: 12 }} disabled={festschreiben.isPending}
          onClick={async () => {
            setFehler(""); setMeldung("");
            try {
              await festschreiben.mutateAsync();
              setMeldung("Festgeschrieben — steht jetzt unter den fälligen Perioden.");
            } catch (e) { setFehler(fehlertext(e) || "Fehlgeschlagen."); }
          }}>
          {festschreiben.isPending ? "Schreibe fest…" : "Schlussabrechnung festschreiben"}
        </button>
      )}
      {meldung && <div style={{ fontSize: 12.5, marginTop: 8, color: "var(--pf-ink-3)" }}>{meldung}</div>}
      {fehler && <div style={{ fontSize: 12.5, marginTop: 8, color: "var(--pf-red)" }}>{fehler}</div>}
    </div>
  );
}

